import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, Search, Edit, ChevronLeft, ChevronRight } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { useAuthStore } from '@/store/auth.store'
import { contractService } from '@/services/contractService'
import { ContractListItem, ContractListResponse, EmployeeOption } from '@/api/types'

/**
 * Contracts List Page
 * Displays a table of contracts with CRUD operations
 */
export const ContractsList: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()
  
  const [contracts, setContracts] = useState<ContractListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [searchQuery, setSearchQuery] = useState('')
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [employees, setEmployees] = useState<EmployeeOption[]>([])

  const branchId = user?.selectedBranch?.branchId

  // Fetch employees using GetAll API for mapping employee names in contract list
  useEffect(() => {
    if (!branchId) {
      console.warn('⚠️ [ContractsList] No branchId, cannot fetch employees')
      return
    }

    const fetchEmployees = async () => {
      try {
        console.log('🔵 [ContractsList] Fetching employees using GetAll API for branchId:', branchId)
        const employeesData = await contractService.getAllEmployees(branchId)
        console.log('✅ [ContractsList] Loaded employees from GetAll API:', employeesData?.length || 0)
        if (employeesData && employeesData.length > 0) {
          console.log('🔵 [ContractsList] Sample employee:', employeesData[0])
        }
        setEmployees(employeesData || [])
      } catch (error: any) {
        console.error('❌ [ContractsList] Error fetching employees for contract list:', error)
        console.error('Error details:', error.response?.data || error.message)
      }
    }

    fetchEmployees()
  }, [branchId])

  // Fetch contracts
  useEffect(() => {
    if (!branchId) {
      toast.error('Please select a branch first')
      return
    }

    const fetchContracts = async () => {
      try {
        setLoading(true)
        const response: ContractListResponse = await contractService.getAllContracts(
          branchId,
          pageNumber,
          pageSize
        )
        console.log('🔵 [ContractsList] Contracts data from API:', response.data)
        setContracts(response.data || [])
        setTotalCount(response.totalRecords || 0)
        // Calculate total pages
        const totalPages = response.totalRecords 
          ? Math.ceil(response.totalRecords / response.pageSize) 
          : 0
        setTotalPages(totalPages)
      } catch (error: any) {
        const status = error.response?.status
        const msg = (error.response?.data?.message || '').toLowerCase()
        const isNoData = status === 404 || /not found|no data|no records|no contract/.test(msg)
        setContracts([])
        setTotalCount(0)
        setTotalPages(0)
        if (!isNoData) {
          toast.error(error.response?.data?.message || 'Failed to fetch contracts')
        }
      } finally {
        setLoading(false)
      }
    }

    fetchContracts()
  }, [branchId, pageNumber, pageSize])

  // Map employee names to contracts using employee GetAll API data
  const contractsWithEmployeeNames = useMemo(() => {
    console.log('🔵 [ContractsList] Mapping employee names - Contracts:', contracts.length, 'Employees:', employees.length)
    
    if (contracts.length === 0) {
      console.log('🔵 [ContractsList] No contracts to map')
      return contracts
    }
    
    if (employees.length === 0) {
      console.warn('⚠️ [ContractsList] No employees loaded yet, cannot map names. Will retry when employees load.')
      return contracts
    }
    
    console.log('🔵 [ContractsList] Sample contract empId:', contracts[0]?.empId)
    console.log('🔵 [ContractsList] Sample employee empId:', employees[0]?.empId)
    
    const mapped = contracts.map((contract) => {
      // If employee name already exists, use it
      if (contract.employeeName && contract.employeeName !== 'N/A') {
        console.log('✅ [ContractsList] Contract', contract.id, 'already has employeeName:', contract.employeeName)
        return contract
      }
      
      // Otherwise, find employee by empId and map name
      if (contract.empId) {
        const employee = employees.find((emp) => {
          const match = emp.empId === contract.empId
          if (!match && contract.empId) {
            console.log('🔍 [ContractsList] Checking empId match - contract:', contract.empId, 'employee:', emp.empId, 'match:', match)
          }
          return match
        })
        
        if (employee) {
          const fullName = employee.fullName || 
            [employee.empFirstNameEn, employee.empSecondNameEn, employee.empLastNameEn]
              .filter(Boolean)
              .join(' ')
              .trim() || 
            `Employee ${contract.empId}`
          console.log('✅ [ContractsList] Mapped employee name for contract', contract.id, '- empId:', contract.empId, '-> Name:', fullName)
          return {
            ...contract,
            employeeName: fullName,
          }
        } else {
          console.warn('⚠️ [ContractsList] Employee not found for contract', contract.id, 'with empId:', contract.empId, 'Available empIds:', employees.map(e => e.empId).slice(0, 5))
        }
      } else {
        console.warn('⚠️ [ContractsList] Contract', contract.id, 'has no empId. Contract data:', contract)
      }
      return contract
    })
    
    console.log('✅ [ContractsList] Mapped contracts with employee names:', mapped.length)
    const withNames = mapped.filter(c => c.employeeName && c.employeeName !== 'N/A').length
    console.log('✅ [ContractsList] Contracts with employee names:', withNames, 'out of', mapped.length)
    return mapped
  }, [contracts, employees])

  // Client-side search filtering - use contractsWithEmployeeNames instead of contracts
  const filteredContracts = useMemo(() => {
    if (!searchQuery.trim()) {
      return contractsWithEmployeeNames
    }

    const query = searchQuery.toLowerCase()
    return contractsWithEmployeeNames.filter((contract) => {
      const employeeName = (contract.employeeName || '').toLowerCase()
      const departmentName = (contract.departmentName || '').toLowerCase()
      const gradeName = (contract.gradeName || '').toLowerCase()
      const projectName = (contract.projectName || '').toLowerCase()
      const status = (contract.status || '').toLowerCase()

      return (
        employeeName.includes(query) ||
        departmentName.includes(query) ||
        gradeName.includes(query) ||
        projectName.includes(query) ||
        status.includes(query)
      )
    })
  }, [contractsWithEmployeeNames, searchQuery])

  const handleAddContract = () => {
    navigate('/contracts/new')
  }

  const handleEditContract = (id: number) => {
    navigate(`/contracts/${id}/edit`)
  }

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize))
    setPageNumber(1) // Reset to first page when page size changes
  }

  const handlePreviousPage = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1)
    }
  }

  const handleNextPage = () => {
    if (pageNumber < totalPages) {
      setPageNumber(pageNumber + 1)
    }
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
    } catch {
      return dateString
    }
  }

  const getStatusBadge = (status?: string, statusId?: number) => {
    if (statusId === 1 || status?.toLowerCase().includes('active')) {
      return <Badge variant="default" className="text-xs">Active</Badge>
    }
    return <Badge variant="secondary" className="text-xs">Not Active</Badge>
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader
          title={t('contracts.title')}
          description={t('contracts.description')}
        />
        <Card>
          <CardContent className="p-6">
            <EmptyState
              title={t('contracts.branchNotSelected')}
              description={t('contracts.selectBranchToView')}
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('contracts.title')}
        description={t('contracts.description')}
        action={{
          label: t('contracts.addNew'),
          onClick: handleAddContract,
          icon: Plus,
        }}
      />

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder={t('contracts.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground whitespace-nowrap">{t('contracts.pageSize')}</span>
              <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contracts Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">{t('contracts.loading')}</p>
            </div>
          ) : filteredContracts.length === 0 ? (
            <div className="p-12">
              <EmptyState
                title={t('contracts.noContractsFound')}
                description={
                  searchQuery
                    ? t('contracts.tryAdjustingSearch')
                    : t('contracts.getStartedAddFirst')
                }
                action={
                  !searchQuery
                    ? {
                        label: t('contracts.addNew'),
                        onClick: handleAddContract,
                      }
                    : undefined
                }
              />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('contracts.employee')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('contracts.contractStartDate')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('contracts.contractEndDate')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('contracts.status')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('contracts.actions')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredContracts.map((contract) => (
                    <TableRow 
                      key={contract.id || contract.contractId} 
                      className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors"
                    >
                      <TableCell className="px-4 py-2 text-sm text-center align-middle">
                        {contract.employeeName || 'N/A'}
                      </TableCell>
                      <TableCell className="px-4 py-2 text-sm text-center align-middle">
                        {formatDate(contract.contractStartDate)}
                      </TableCell>
                      <TableCell className="px-4 py-2 text-sm text-center align-middle">
                        {formatDate(contract.contractEndDate)}
                      </TableCell>
                      <TableCell className="px-4 py-2 text-sm text-center align-middle">
                        {getStatusBadge(contract.status, contract.contractstatus)}
                      </TableCell>
                      <TableCell className="px-4 py-2 text-center align-middle">
                        <div className="flex justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditContract(contract.id || contract.contractId || 0)}
                            className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="border-t px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    {searchQuery ? (
                      <span>
                        {t('contracts.showing')} {filteredContracts.length} {t('contracts.of')} {totalCount} {t('contracts.contracts_count')}
                        {searchQuery && ` (filtered from ${totalCount} total)`}
                      </span>
                    ) : (
                      <span>
                        {t('contracts.showing')} {(pageNumber - 1) * pageSize + 1} {t('contracts.to')}{' '}
                        {Math.min(pageNumber * pageSize, totalCount)} {t('contracts.of')} {totalCount} {t('contracts.contracts_count')}
                      </span>
                    )}
                  </div>
                  {!searchQuery && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePreviousPage}
                        disabled={pageNumber === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        {t('contracts.previous')}
                      </Button>
                      <div className="text-sm">
                        Page {pageNumber} {t('contracts.of')} {totalPages || 1}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleNextPage}
                        disabled={pageNumber >= totalPages}
                      >
                        {t('contracts.next')}
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
